import { requireService } from '@profgeosoft/di-ez';
import { useCallback, useRef, useState } from 'react';

import { wrap } from 'src/packages/mobx-di/wrap';
import { mergeRefs } from 'src/packages/utils/merge-refs';

import type { GridRowParams, GridRowId } from '@repo/go-table';
import type { ReactNode, RefCallback } from 'react';
import type { DirectoryRecord } from 'src/services/directory-service/entities/directory-record.entity';
import type { MainDirectoryService } from 'src/services/directory-service/main-directory-service';

import { SubTableComponent } from '../../controls/sub-table-component';

import styles from './detail-panel.module.scss';

type Props = {
  params: GridRowParams<DirectoryRecord>;
  directory: MainDirectoryService;
  outerRefCb: RefCallback<HTMLDivElement>;
};

type TDetailPanelHookReturnType = {
  getDetailPanelContent: ((params: GridRowParams<DirectoryRecord>) => ReactNode) | null;
  getPanelHeight(params: GridRowParams<DirectoryRecord>): number | 'auto';
};

const DetailPanel = wrap<Props>(function DetailPanel({ params, directory, outerRefCb }) {
  const panelRef = useRef<HTMLDivElement | null>(null);
  const record = directory.recordsMap.get(params.id);
  const complexControlViews = directory.complexControlsView;
  const { tableSettings } = requireService('directoryPage');

  if (!record) {
    console.error('cannot find record');
    return null;
  }

  return (
    <div ref={mergeRefs(panelRef, outerRefCb)} className={styles.wrapper}>
      {complexControlViews.map((view) => {
        switch (view.control) {
          case 'Table': {
            const initialValue = record.initialData?.[view.fieldId] ?? [];

            if (!Array.isArray(initialValue)) {
              console.error('wrong subtable initial value');
              return null;
            }

            return (
              <SubTableComponent
                key={view.fieldId}
                parentDirectoryService={directory}
                record={record}
                view={view}
                tableSettingsService={tableSettings}
                optionsService={directory.optionsService}
                initialValue={initialValue}
              />
            );
          }
        }
      })}
    </div>
  );
});

export const useDetailPanel = (directory: MainDirectoryService): TDetailPanelHookReturnType => {
  const [panelRefsMap] = useState(() => new Map<GridRowId, HTMLDivElement>());

  const _getDetailPanelContent = useCallback(
    (params: GridRowParams<DirectoryRecord>) => {
      if (!directory.complexControlsView?.length) {
        return null;
      }

      return (
        <DetailPanel
          outerRefCb={(element: HTMLDivElement | null) => {
            if (element) {
              panelRefsMap.set(params.id, element);
            }
          }}
          params={params}
          directory={directory}
        />
      );
    },
    [directory, panelRefsMap],
  );

  const getPanelHeight = (params: GridRowParams<DirectoryRecord>): number | 'auto' => {
    return 532;
  };

  return {
    getDetailPanelContent: directory.complexControlsView?.length ? _getDetailPanelContent : null,
    getPanelHeight,
  };
};
